import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import AccordionIssuedAssessmentForm, { VerificationMessiVM, schema } from './form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { DateTime } from 'luxon';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';
import { AttachmentsData, PracticeData } from 'shell/models/practice';
import { HttpResponse } from 'shell/models/http-response';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { Outcome } from 'practice/constants/outcome';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import ModalPrintSuccess from '../print-comunication/modal';
import { ModalPdfViewerState } from 'shell/view-models/pdf-modal';
import { AxiosResponse } from 'axios';
import ErrorModal from 'shell/components/shared/modal-error';
import ModalDigitalManuel from './modal-digital-manuel';

interface VerificationMessiProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
  loadViewMessi: () => Promise<void>;
  isUnavailability?: boolean;
  noteMesso?: string;
  datiMesso?: PracticeData;
}

const VerificationMessiComponent = ({
  loadUpdateDate,
  isUnavailability,
  noteMesso,
  loadViewMessi,
  datiMesso,
}: VerificationMessiProps): React.ReactElement => {
  const params = useParams();

  const [open, setOpen] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const [modalError, setModalError] = useState<{ message?: string; isError?: boolean }>({
    message: '',
    isError: false,
  });

  const controlPrintNotify = useHttpClient<
    HttpResponse<{
      messaggio: string;
      showMessage: boolean;
    }>
  >(`${__PRACTICE_API_URL__}/accertamento-messi/controlli-stampa-notifica`, {
    options: {
      method: 'POST',
      errorCatching: true,
      loading: true,
    },
  });

  const insertData = useHttpClient(`${__PRACTICE_API_URL__}/accertamento-messi/ritorno`, {
    options: {
      method: 'POST',
      errorCatching: true,
      loading: true,
    },
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const sendData = useEventCallback((values: VerificationMessiVM) => {
    let nuovoIndirizzoIntestatario = '';

    if (values.addressAuto?.description) {
      nuovoIndirizzoIntestatario =
        nuovoIndirizzoIntestatario + (values.addressAuto?.description || '');
    }

    if (values.number) {
      nuovoIndirizzoIntestatario = `${nuovoIndirizzoIntestatario} ${values.number || ''}`;
    }

    if (formContext.values.exponent) {
      nuovoIndirizzoIntestatario =
        nuovoIndirizzoIntestatario + transformExponent(formContext.values.exponent || '');
    }

    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      // tipoPratica: params.tipoPratica || undefined,
      timestampSospensione: values.date ? values.date?.toFormat('yyyy-MM-dd') : undefined,
      tipoEsito: values.outcome || undefined,
      note: values.note || undefined,
      nuovoIndirizzoIntestatario: nuovoIndirizzoIntestatario || undefined,
      // tipoOperazione: '10',
      // codiceServizio: userProfile.datiLogin?.profile?.servizio?.key.codiceServizio || undefined,
      // codiceOperatore: userProfile.datiLogin?.key?.userName || undefined,
      // tipoStatoPratica: '1',
      // codiceSospensione: 2,
    };

    (insertData({ data }) as ResponsePromise<void>).then(async () => {
      toast.success('Operazione eseguita con successo');

      // aggiornamento stato pratica
      formContextPage.setFieldValue('practice', {
        ...(formContextPage.values.practice || {}),
        codiceStatoPratica: '1',
        tipoOperazione: '10',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContextPage.setValues({
          ...(formContextPage.values || {}),
          ...newData,
        });
      }

      loadViewMessi();
    });
  });

  const onSubmit = useEventCallback(async (values: VerificationMessiVM) => {
    if (values.proprietari && !values.struttura) {
      try {
        const response = await controlPrintNotify({
          data: {
            codiceEnte: params.codiceEnte,
            tipoRegistro: params.tipoRegistro,
            idAnnoPratica: params.idAnnoPratica,
            progressivoPratica: params.progressivoPratica,
            tipoEsito: '1',
            proprietari:
              values.proprietari
                ?.filter((el) => !el.disabled)
                ?.map((el) => ({
                  ...el.proprietarioBE,
                  flagRichiestaNotifica: '1',
                })) || undefined,
          },
        });
        const castedResponse = response as AxiosResponse<
          HttpResponse<{ showMessage: boolean; messaggio: string }>
        >;

        if (castedResponse.data.payload.showMessage) {
          setModalError({ isError: true, message: castedResponse.data.payload.messaggio });
          return;
        }
      } catch (error) {
        return;
      }

      sendData(values);
    } else if (values.outcome === '1') {
      // Se accertamento POSITIVO
      setOpenModalDigitalManual(true);
    } else {
      sendData(values);
    }
  });

  const formContext = useFormik<VerificationMessiVM>({
    initialValues: {
      date: datiMesso?.timestampOperazione
        ? DateTime.fromISO(datiMesso?.timestampOperazione)
        : DateTime.now(),
      outcome: datiMesso?.esito || '',
      note: '',
      noteMesso: noteMesso || '',
      addressAuto: null,
      cap: '',
    },
    validationSchema: schema,
    onSubmit,
    // enableReinitialize: true,
  });

  const [pdfModal, setPdfModal] = useState<
    Partial<ModalPdfViewerState> & { isActButton?: boolean; isDigitalManual?: boolean }
  >({
    data: '',
    open: false,
    name: '',
  });

  const [pdfModalAddress, setPdfModalAddress] = useState<Partial<ModalPdfViewerState>>({
    data: '',
    open: false,
    name: '',
  });
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    data?: string;
  }>({
    open: false,
  });

  const [openModalDigitalManual, setOpenModalDigitalManual] = useState<boolean>(false);

  const printAct = useHttpClient(`${__PRACTICE_API_URL__}/accertamento-messi/visualizza-atto`, {
    options: {
      method: 'POST',
      errorCatching: true,
      loading: true,
    },
  });

  const onClickAct = useEventCallback(() => {
    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
    };

    (
      printAct({
        data,
      }) as ResponsePromise<AttachmentsData>
    ).then((response) => {
      const responseCasted = (response?.data as HttpResponse<AttachmentsData>)
        ?.payload as AttachmentsData;

      const pdfCertificate = responseCasted.contenutoAllegato;

      if (pdfCertificate) {
        // const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
        // blobToBase64(blob).then((base64) => {
        //   setPdfModal({
        //     data: base64.split(',')[1],
        //     name: responseCasted.nomeFile || responseCasted.descrizioneFile || '',
        //     open: true,
        //   });
        // });
        setPdfModal({
          data: responseCasted.contenutoAllegato as string,
          name: responseCasted.nomeFile || responseCasted.descrizioneFile || '',
          open: true,
          isActButton: true,
          title: 'Stampa Atto',
        });
      }
      toast.success('Operazione eseguita con successo');
    });
  });

  const printDataUnavailability = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/stampa/proposta-cambio-indirizzo`,
    {
      options: {
        method: 'POST',
        errorCatching: true,
        loading: true,
      },
    }
  );

  const printData = useEventCallback(() => {
    let nuovoIndirizzoIntestatario = '';

    if (formContext.values.addressAuto?.description) {
      nuovoIndirizzoIntestatario =
        nuovoIndirizzoIntestatario + (formContext.values.addressAuto?.description || '');
    }

    if (formContext.values.number) {
      nuovoIndirizzoIntestatario = `${nuovoIndirizzoIntestatario} ${
        formContext.values.number || ''
      }`;
    }

    if (formContext.values.exponent) {
      nuovoIndirizzoIntestatario =
        nuovoIndirizzoIntestatario + transformExponent(formContext.values.exponent || '');
    }

    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      nuovoIndirizzoIntestatario: nuovoIndirizzoIntestatario || undefined,
      capNuovoIndirizzoIntestatario: formContext.values.cap,
    };

    (printDataUnavailability({ data, responseType: 'blob' }) as ResponsePromise<Blob>).then(
      (response) => {
        const pdfCertificate = response?.data;

        if (pdfCertificate) {
          const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
          blobToBase64(blob).then((base64) => {
            setPdfModalAddress({
              data: base64.split(',')[1],
              name: 'Stampa proposta cambiamento indirizzo',
              open: true,
            });
          });
        }
        toast.success('Operazione eseguita con successo');
      }
    );
  });

  const onPrintNotificationStructure = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/accertamento-messi/stampa-notifica-struttura`,
    {
      options: {
        method: 'POST',
        errorCatching: true,
        loading: true,
      },
    }
  );

  const printNotificationStructure = useEventCallback((tipoStampa: string) => {
    setOpenModalDigitalManual(false);

    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      tipoEsito: formContext.values.outcome,
      tipoStampa,
    };

    (onPrintNotificationStructure({ data, responseType: 'blob' }) as ResponsePromise<Blob>).then(
      (response) => {
        const pdfCertificate = response?.data;

        if (pdfCertificate) {
          const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1],
              name: 'Notifica',
              title: 'Notifica',
              open: true,
              isDigitalManual: true,
            });
          });
        }
        toast.success('Operazione eseguita con successo');
      }
    );
  });

  const occupanteLegittimo =
    formContextPage.values.address?.italianAddress?.occupantiLegittimi || null;

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-issued-assessment-content"
          id="panel-issued-assessment-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Ritorno accertamento messi</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionIssuedAssessmentForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            onChanges={(values) => {
              formContext.setValues({
                ...formContext.values,
                ...values,
              });
            }}
            datiMesso={datiMesso}
            errors={formContext.errors}
            touched={formContext.touched}
            isUnavailability={isUnavailability}
          />
          <ErrorModal
            title="Attenzione"
            message={modalError.message || ''}
            open={modalError.isError || false}
            onClose={() => {
              setModalError({ isError: false, message: '' });
              sendData(formContext.values);
            }}
          />
          <div className="pmi-d-flex">
            <div className="pmi-align-right pmi-pt-2">
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                onClick={() => {
                  formContext.handleSubmit();
                }}
                id="return"
              >
                Conferma
              </PmiButton>
            </div>
            <div className="pmi-align-right pmi-pl-2 pmi-pt-2">
              <PmiButton color="cdmBrandThird" variant="outlined" onClick={onClickAct}>
                {"Atto dell'accertamento"}
              </PmiButton>
            </div>
            <ModalViewerPDF
              open={pdfModal.open || false}
              data={pdfModal.data || ''}
              name={pdfModal.name || ''}
              title={pdfModal.title || 'Stampa atto'}
              onClose={() => {
                if (!pdfModal.isActButton) {
                  setModalPrintSuccess((oldState) => ({
                    ...oldState,
                    data: pdfModal.data,
                    open: true,
                  }));
                }

                setPdfModal({
                  data: '',
                  open: false,
                  name: '',
                  isDigitalManual: pdfModal.isDigitalManual,
                });
              }}
            />
            {modalPrintSuccess.open ? (
              <ModalPrintSuccess
                open={modalPrintSuccess.open}
                onYes={() => {
                  if (pdfModal.isDigitalManual) {
                    setPdfModal({ open: false });
                    setModalPrintSuccess({ open: false });
                    sendData(formContext.values);
                  } else {
                    setPdfModal({ open: false });
                    formContext.handleSubmit();
                  }
                }}
                onClose={() => {
                  if (!pdfModal.isDigitalManual) {
                    setPdfModal({
                      title: 'Stampa Comunicazione',
                      name: 'Stampa comunicazione',
                      data: modalPrintSuccess.data || '',
                      open: true,
                    });
                  }
                  setModalPrintSuccess({ open: false });
                }}
              />
            ) : undefined}
            {openModalDigitalManual ? (
              <ModalDigitalManuel
                open={openModalDigitalManual}
                onClose={() => setOpenModalDigitalManual(false)}
                onDigital={() => printNotificationStructure('D')}
                onManual={() => printNotificationStructure('X')}
                tipoRegistro={params.tipoRegistro || undefined}
                occupanteLegittimo={occupanteLegittimo}
                onUnsolicitedNotification={() => sendData(formContext.values)}
              />
            ) : undefined}
            {isUnavailability && formContext.values.outcome === Outcome.CambioIndirizzo ? (
              <div className="pmi-align-right pmi-pl-2 pmi-pt-2">
                <PmiTooltipError
                  show={!formContext.values.addressAuto || !formContext.values.cap}
                  message={
                    !formContext.values.addressAuto || !formContext.values.cap
                      ? 'Inserire indirizzo e cap'
                      : ''
                  }
                >
                  <span>
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      onClick={printData}
                      id="print"
                      disabled={!formContext.values.addressAuto || !formContext.values.cap}
                    >
                      Stampa proposta cambiamento indirizzo
                    </PmiButton>
                  </span>
                </PmiTooltipError>

                <ModalViewerPDF
                  open={pdfModalAddress.open || false}
                  data={pdfModalAddress.data || ''}
                  name={pdfModalAddress.name || ''}
                  title="Stampa proposta cambiamento indirizzo"
                  onClose={() => {
                    setPdfModalAddress({ data: '' });
                  }}
                />
              </div>
            ) : undefined}
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

VerificationMessiComponent.displayName = 'Accordion Verification Messi component';

const VerificationMessi = memo(VerificationMessiComponent);

export default VerificationMessi;
